import gql from "graphql-tag"
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  Observable,
} from "@apollo/client"
import { BatchHttpLink } from "@apollo/client/link/batch-http"
import { onError } from "@apollo/client/link/error"

import { getIdToken, onAuthStateChanged } from "firebase/auth"
import possibleTypes from "./possibleTypesNext.json"
import { graphqlNextUrl } from "./config"
import { sdk9Auth } from "./firebase"
import { logError, logErrorMessage } from "./utils/errors"
import { CreateSessionCookieDocument } from "./ApolloClientNext.graphqlTypes"

gql`
  mutation CreateSessionCookie($session: CreateSessionCookieInput!) {
    createSessionCookie(session: $session)
  }
`

const OPERATIONS_TO_IGNORE_NETWORK_ERROR = ["updateStudentCourseLectureSession"]

async function getUserIdToken() {
  if (sdk9Auth.currentUser) {
    const token = await getIdToken(sdk9Auth.currentUser, true)
    return token
  }

  let unsub: () => void

  const getTokenTask = new Promise<string>((resolve, reject) => {
    unsub = onAuthStateChanged(sdk9Auth, async (user) => {
      try {
        if (user) {
          const token = await getUserIdToken()
          unsub()
          resolve(token)
        }
      } catch (error) {
        reject(error)
        unsub()
      }
    })
  })
  const timeoutTask = new Promise<string>((resolve, reject) => {
    setTimeout(() => {
      if (unsub) {
        unsub()
      }
      reject(new Error("Unable to get firebase user"))
    }, 5000)
  })
  return Promise.race([getTokenTask, timeoutTask])
}

let sessionCookieCache: Promise<string> | undefined
export async function requestSessionCookie() {
  if (sessionCookieCache) {
    return sessionCookieCache
  }

  async function run() {
    const idToken = await getUserIdToken()
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const response = await client.mutate({
      mutation: CreateSessionCookieDocument,
      variables: {
        session: {
          idToken,
        },
      },
    })

    if (!response.data) {
      throw new Error("Unable to get session token")
    }

    return response.data.createSessionCookie
  }

  sessionCookieCache = run()

  setTimeout(() => {
    sessionCookieCache = undefined
  }, 5000)

  return sessionCookieCache
}

const refreshTokenLink = onError(
  ({ graphQLErrors, operation, networkError, forward }) => {
    if (!graphQLErrors && !networkError) {
      return
    }

    const authError = graphQLErrors?.find(
      (error) =>
        error.extensions?.code === "NOT_SIGNED_IN" ||
        error.message === "Custom price is only available to admins",
    )

    if (!authError) {
      const extras = {
        operationName: operation.operationName,
        variables:
          operation.operationName === "CreateSessionCookie"
            ? "obfuscated"
            : operation.variables,
      }

      graphQLErrors?.forEach(({ message }) => {
        logErrorMessage(message, extras)
      })

      if (networkError) {
        // These calls are bogging down the error log so removing for now
        if (OPERATIONS_TO_IGNORE_NETWORK_ERROR.includes(extras.operationName)) {
          return
        }
        logError(networkError, extras)
      }

      return
    }

    // refresh token through async request
    // eslint-disable-next-line consistent-return
    return new Observable((observer) => {
      requestSessionCookie()
        .then(() => {
          const subscriber = {
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          }

          // retry last failed request
          forward(operation).subscribe(subscriber)
        })
        .catch(observer.error)
    })
  },
)

const waitForSessionCookieLink = new ApolloLink((operation, forward) => {
  const promise = sessionCookieCache

  if (operation.operationName === "CreateSessionCookie" || !promise) {
    return forward(operation)
  }

  return new Observable((observer) => {
    promise
      .then(() => {
        const subscriber = {
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        }

        forward(operation).subscribe(subscriber)
      })
      .catch(observer.error)
  })
})

const httpLink = new BatchHttpLink({
  uri: graphqlNextUrl,
  credentials: "include",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  batchMax: 10,
  batchInterval: 20,
})

const cache = new InMemoryCache({ possibleTypes })

const client = new ApolloClient({
  cache,
  link: ApolloLink.from([refreshTokenLink, waitForSessionCookieLink, httpLink]),
})

export default client
